/**
 * Single source of truth for the lobby board crossfade cadence.
 *
 * `LobbyBoardStage` mounts two slots simultaneously during a board
 * navigation: the outgoing slot keeps painting at opacity 1 while the
 * incoming slot mounts offscreen at opacity 0, runs its data fetch +
 * forfeit baseline + first paint, and only then trips the visible
 * crossfade. The numbers below are tuned for that flow:
 *
 *  - `BOARD_CROSSFADE_MS` is the duration of the synchronized opacity
 *    transition that both slots animate in lockstep. Long enough to
 *    read as a deliberate hand-off (matches the original 2D fade
 *    cadence), short enough that rapid browsing through three or four
 *    boards doesn't feel sluggish. The CSS rule in `globals.css`
 *    consumes this value via the `--board-crossfade-ms` custom
 *    property the stage emits; keeping the number here lets the JS
 *    timer that schedules the land audio cue stay in sync without an
 *    extra import dance.
 *  - `BOARD_PRELOAD_TIMEOUT_MS` is the hard ceiling on how long the
 *    incoming slot can sit at opacity 0 waiting for its readiness
 *    gate. Past this we fall through into a hard cut so a stuck
 *    indexer / network hiccup can't wedge the user on the outgoing
 *    board. 2 s is comfortably under any "did the click register?"
 *    threshold while still giving warm-cache hits + cold-start fetches
 *    plenty of room.
 *  - `BOARD_LAND_AUDIO_DELAY_MS` is when `hqPlayBoardLand()` fires
 *    relative to `hqPlayBoardWhoosh()` — the visible crossfade is
 *    `BOARD_CROSSFADE_MS` long, so the land cue lines up exactly with
 *    the new slot reaching opacity 1.
 */
export const BOARD_CROSSFADE_MS = 400;

/** See module doc for `BOARD_PRELOAD_TIMEOUT_MS` rationale. */
export const BOARD_PRELOAD_TIMEOUT_MS = 2000;

/** See module doc for `BOARD_LAND_AUDIO_DELAY_MS` rationale. */
export const BOARD_LAND_AUDIO_DELAY_MS = BOARD_CROSSFADE_MS;
